import React, { useContext, useMemo, useState } from 'react'

// @mui core components
import { Box, Grid, Typography, Button } from '@mui/material'
import Card from 'atoms/Card'

// Data
import { selectData } from 'layouts/onboarding'
import { IntakeContext } from 'context/IntakeContext'

// Popup
import { toast } from 'react-toastify'
// import { renderMuiFormField } from 'utils/forms'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'
import zipState from 'zip-state'

function BasicInfo() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your user profile', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [localForm, setLocalForm] = useState({
    firstName: intakeState.firstName || '',
    lastName: intakeState.lastName || '',
    phone: intakeState.phone || '',
    dateOfBirth: intakeState.dateOfBirth || '',
    address: intakeState.address || '',
    address2: intakeState.address2 || '',
    city: intakeState.city || '',
    state: intakeState.state || '',
    zipcode: intakeState.zipcode || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const stateLogo = useMemo(() => {
    return (
      selectData.states.find(({ value }) => value === intakeState?.state)
        ?.logo || ''
    )
  }, [intakeState?.state])
  const stateFlagStyle = useMemo(
    () => ({
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
        {
          display: 'flex',
        },
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
        {
          content: '""',
          width: '40px',
          height: '24px',
          marginRight: '8px',
          marginTop: '2px',
          backgroundImage: `url("${stateLogo}")`,
          backgroundSize: 'cover',
        },
    }),
    [stateLogo]
  )

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          PERSONAL
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,
                id: 'firstName',
                label: 'First Name',
                placeholder: 'First Name',
                inputType: 'text',
                value: localForm.firstName,
                onChange: (e) => handleChange('firstName')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({
                    firstName: val,
                    fullName: `${val} ${intakeState.lastName}`,
                  })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'lastName',
                label: 'Last Name',
                placeholder: 'Last Name',
                inputType: 'text',
                value: localForm.lastName,
                onChange: (e) => handleChange('lastName')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({
                    lastName: val,
                    fullName: `${intakeState.firstName} ${val}`,
                  })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'email',
                label: 'Email',
                placeholder: 'Email',
                inputType: 'text',
                value: intakeState.email,
                disabled: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'phone',
                label: 'Phone Number',
                placeholder: 'Eg. xxx-xxx-xxxx',
                inputType: 'tel',
                value: localForm.phone,
                disabled: true,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'dateOfBirth',
                label: 'Date of Birth',
                placeholder: '01/01/1970',
                inputType: 'date',
                value: localForm.dateOfBirth,
                onChange: (e) => handleChange('dateOfBirth')(e),
                onBlur: updateIntakeStateAndSubmit((dateOfBirth) =>
                  updateIntakeStateAtKey({ dateOfBirth: dateOfBirth })
                ),
              })}
            </Grid>
          </Grid>
          <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'address',
                label: 'Street Address',
                placeholder: '12345 Main Street',
                inputType: 'text',
                value: localForm.address,
                onChange: (e) => handleChange('address')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({ address: val })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'address2',
                label: 'Apt, Suite, Bldg. (optional)',
                placeholder: 'Apt, Suite, Bldg. (optional)',
                inputType: 'text',
                value: localForm.address2,
                onChange: (e) => handleChange('address2')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({ address2: val })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderMuiFormField({
                fullWidth: true,
                id: 'city',
                label: 'City',
                placeholder: 'City',
                inputType: 'text',
                value: localForm.city,
                onChange: (e) => handleChange('city')(e),
                onBlur: updateIntakeStateAndSubmit((val) =>
                  updateIntakeStateAtKey({ city: val })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={4} sx={stateFlagStyle}>
              {renderMuiFormField({
                fullWidth: true,
                showAdornment: false,
                autoFocus: false,
                id: 'state',
                label: 'State',
                placeholder: 'State',
                inputType: 'select',

                value: intakeState.state,
                onChange: updateIntakeStateAndSubmit((value) =>
                  updateIntakeStateAtKey({ state: value })
                ),
                options: selectData.states,
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderMuiFormField({
                fullWidth: true,

                id: 'zipcode',
                label: 'Zipcode',
                placeholder: '#####',
                inputType: 'zipcode',
                value: localForm.zipcode,
                onChange: (e) => handleChange('zipcode')(e),
                onBlur: updateIntakeStateAndSubmit((zipcode) =>
                  updateIntakeStateAtKey({
                    zipcode: zipcode,
                    state:
                      selectData.states.find(
                        (s) => s.abbr === zipState(zipcode)
                      )?.value || null,
                  })
                ),
              })}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(BasicInfo)
